/**
 * Avatar framing: how an uploaded photo is cropped into a circle.
 *
 * The uploaded file is never re-encoded or cut down. A phone photo is a
 * rectangle of a whole person; an avatar is a small circle that should hold
 * their face. Rather than destroy the original to fit, the record stores how
 * to FRAME it — a zoom and an offset from centre — and every avatar in the
 * product applies the same framing at whatever size it draws. One adjustment
 * therefore fixes the header, the roster, the register, the till and the
 * profile at once, and it can be changed or cleared forever afterwards
 * because the original pixels are still there.
 *
 * Offsets are percentages of the frame rather than pixels so the framing is
 * identical whether the avatar is drawn at 28px or 240px.
 */

export interface PhotoTransform {
  /** 1 fills the frame; up to 4 moves in closer. */
  zoom: number
  /** Percent of the frame, from its centre. */
  offsetX: number
  offsetY: number
}

export const PHOTO_ZOOM_MIN = 1
export const PHOTO_ZOOM_MAX = 4
/** How far off-centre a photo may be pushed before the frame shows through. */
export const PHOTO_OFFSET_LIMIT = 50

const clampNumber = (value: number, min: number, max: number) =>
  Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : min

/**
 * Brings any proposed framing inside the legal range, rounded to a tenth —
 * the server never trusts a client's numbers, and a transform is meaningless
 * if it pushes the picture out of its own frame.
 */
export function clampPhotoTransform(input: Partial<PhotoTransform> | null | undefined): PhotoTransform | null {
  if (!input) return null
  const zoom = Math.round(clampNumber(input.zoom ?? 1, PHOTO_ZOOM_MIN, PHOTO_ZOOM_MAX) * 100) / 100
  const offsetX = Math.round(clampNumber(input.offsetX ?? 0, -PHOTO_OFFSET_LIMIT, PHOTO_OFFSET_LIMIT) * 10) / 10
  const offsetY = Math.round(clampNumber(input.offsetY ?? 0, -PHOTO_OFFSET_LIMIT, PHOTO_OFFSET_LIMIT) * 10) / 10
  // Dead-centre and unzoomed is the same as having no framing at all.
  if (zoom === 1 && offsetX === 0 && offsetY === 0) return null
  return { zoom, offsetX, offsetY }
}

/**
 * The CSS the browser applies to the image inside a square, clipped frame.
 * Shared so the adjuster a user drags and every avatar that renders the
 * result can never disagree about what the saved numbers mean.
 */
export function photoTransformCss(transform: PhotoTransform | null | undefined): string {
  if (!transform) return 'translate(0%, 0%) scale(1)'
  return `translate(${transform.offsetX}%, ${transform.offsetY}%) scale(${transform.zoom})`
}
